import { useMemo } from 'react';
import {
  ResponsiveContainer, ComposedChart, Bar, ReferenceLine, CartesianGrid, XAxis, YAxis, Tooltip as ChartTooltip,
} from 'recharts';
import { SectionCard, fmtNum, fmtTRY } from '../yeni-design/primitives';
import { demoTheme } from '../../theme';
import type { Donem } from './donem';
import type { SatisKaynaklari } from './ekranKaynaklari';
import { parametreBildiriyor, useSaSorgu } from './useSatisVerisi';
import { KanalKapsamNotu, KapsamNotu } from './KapsamNotu';
import { BlokIskeleti } from './BlokIskeleti';
import { fisOzeti, sayi, type KanalSatiri, type Sayisal } from './ozetHesaplari';

interface SaatlikSatiri {
  saat: Sayisal;
  fis_sayisi: Sayisal;
  ciro: Sayisal;
}

export interface Nokta {
  etiket: string;
  ciro: number;
}

/** ISO tarih -> GG.AA.YYYY (sayfa basligindaki aralikla ayni bicim). Cozulemeyen HAM doner. */
export function tarihEtiketi(ham: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(ham);
  if (!m) return ham;
  return `${m[3]}.${m[2]}.${m[1]}`;
}

/** 990002 SQL 137'den sonra 'HH:00' doner; eski sayisal saat de kabul edilir. */
export function saatEtiketi(ham: unknown): string {
  const s = String(ham ?? '');
  return /^\d{1,2}$/.test(s) ? `${s.padStart(2, '0')}:00` : s;
}

export function ortalama(noktalar: Nokta[]): number | null {
  if (noktalar.length === 0) return null;
  return noktalar.reduce((t, n) => t + n.ciro, 0) / noktalar.length;
}

interface Props {
  kaynaklar: Pick<SatisKaynaklari, 'kanal040' | 'saatlik002'>;
  donem: Donem;
  sube?: string | null;
  kanal?: string | null;
}

/**
 * Satir 2 — trend (5/12). Cok gunlu: 990040 gunluk Σ ciro sutunlari + ortalama cizgisi (040 anahtari
 * ozet/kanal/sube ile paylasilir; sube/kanal istemcide). Gunluk/Dun: 990002 saatlik, `@KANAL` ile.
 * Ortalama cizgisi: toplu fatura gunleri (ör. 31 Agustos) trendi ezer (spec §9).
 */
export function ZamanBlok({ kaynaklar, donem, sube = null, kanal = null }: Props) {
  const saatlikMi = donem.tekGun;
  // 040 TEK anahtar (final-fix F-3a): sube/kanal ASLA parametre olmaz, ds bildirse bile — istemcide suzulur.
  const gunlukQ = useSaSorgu<KanalSatiri>(kaynaklar.kanal040, { donem, sube: null, kanal: null }, !saatlikMi);
  const saatlikQ = useSaSorgu<SaatlikSatiri>(kaynaklar.saatlik002, { donem, sube, kanal }, saatlikMi);

  const noktalar = useMemo<Nokta[]>(() => {
    if (saatlikMi) {
      return (saatlikQ.data?.data ?? []).map((r) => ({ etiket: saatEtiketi(r.saat), ciro: sayi(r.ciro) }));
    }
    return fisOzeti(gunlukQ.data?.data ?? [], sube, kanal).gunluk.map((g) => ({
      etiket: tarihEtiketi(g.tarih),
      ciro: g.ciro,
    }));
  }, [saatlikMi, saatlikQ.data, gunlukQ.data, sube, kanal]);

  const kaynak = saatlikMi ? kaynaklar.saatlik002 : kaynaklar.kanal040;
  const yukleniyor = saatlikMi ? saatlikQ.isLoading : gunlukQ.isLoading;
  const baslik = saatlikMi ? 'Saatlik satış dağılımı' : 'Ciro trendi';

  if (!kaynak) return null;
  if (yukleniyor) return <BlokIskeleti testid="blok-zaman-iskelet" baslik={baslik} yukseklik={240} />;
  if (noktalar.length === 0) return null;

  const ort = ortalama(noktalar);

  return (
    <div data-testid="blok-zaman">
      <SectionCard
        title={baslik}
        subtitle={
          <>
            {saatlikMi ? 'Günün saatlerine göre ciro' : 'Günlük ciro'} · ortalama{' '}
            <span data-testid="zaman-ortalama">{ort === null ? '—' : fmtTRY(ort)}</span>
            {saatlikMi && <KapsamNotu sube={sube} destekli={parametreBildiriyor(kaynaklar.saatlik002, '@LOKASYON')} />}
            {/* Cok gunlu 040 kanali istemcide suzer: not yalniz saatlik 002 icin anlamli. */}
            <KanalKapsamNotu
              kanal={saatlikMi ? kanal : null}
              destekli={parametreBildiriyor(kaynaklar.saatlik002, '@KANAL')}
            />
          </>
        }
      >
        <div style={{ width: '100%', height: 200 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={noktalar} margin={{ top: 4, right: 12, left: 4, bottom: 4 }}>
              <CartesianGrid strokeDasharray="2 4" stroke={demoTheme.border} vertical={false} />
              <XAxis
                dataKey="etiket"
                tick={{ fontSize: 11, fill: demoTheme.textMuted }}
                axisLine={{ stroke: demoTheme.border }}
                tickLine={false}
                minTickGap={24}
                interval="preserveStartEnd"
              />
              <YAxis
                tickFormatter={(v) => fmtNum(Number(v))}
                tick={{ fontSize: 11, fill: demoTheme.textMuted }}
                axisLine={false}
                tickLine={false}
                width={64}
              />
              <ChartTooltip formatter={(v) => fmtTRY(Number(v))} labelFormatter={(l) => String(l)} />
              <Bar dataKey="ciro" fill={demoTheme.primary} radius={[2, 2, 0, 0]} isAnimationActive={false} />
              {ort !== null && <ReferenceLine y={ort} stroke={demoTheme.warning} strokeDasharray="4 4" />}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </SectionCard>
    </div>
  );
}
